<template>
    <div class="messages-wrap">
        <div
            ref="scrollEl"
            class="messages"
            @scroll="onScroll"
        >
            <div ref="innerEl" class="messages-inner">
                <div v-if="showHistoryLoader" class="history-loader">
                    <ChatPreloader
                        label="Загружаем историю диалога…"
                        variant="spinner"
                    />
                </div>

                <template v-else-if="!showHistoryLoader">
                    <div v-if="!messages.length" class="empty">
                        <div class="empty-title">Спросите AI-аналитика</div>
                        <p class="empty-text">
                            Узнайте про динамику рынка, топ марок, регионы, прогноз 2026
                            или задайте свой вопрос. Ответ придёт в режиме реального времени
                            со ссылками на источники.
                        </p>
                        <div class="empty-chips">
                            <button
                                v-for="suggestion in suggestions"
                                :key="suggestion.id"
                                type="button"
                                class="empty-chip"
                                @click="$emit('suggest', suggestion.prompt)"
                            >
                                {{ suggestion.prompt }}
                            </button>
                        </div>
                    </div>

                    <ChatMessage
                        v-for="message in messages"
                        :key="message.id"
                        :message="message"
                        :user-initials="userInitials"
                        :user-name="userName"
                        :selection-mode="selectionMode"
                        :is-selected="selectedIds.includes(message.id)"
                        :is-streaming="isStreaming"
                        :active-message-id="activeMessageId"
                        @regenerate="$emit('regenerate', $event)"
                        @copy="$emit('copy', message)"
                        @feedback="$emit('feedback', message, $event)"
                        @followup="$emit('followup', message, $event)"
                        @toggle-select="$emit('toggle-select', $event)"
                        @update-card-view="$emit('update-card-view', $event)"
                        @edit="$emit('edit', $event)"
                        @version-prev="$emit('version-prev', $event)"
                        @version-next="$emit('version-next', $event)"
                    />
                </template>
            </div>
        </div>

        <button
            v-if="showJump"
            type="button"
            class="jump-button"
            @click="jumpToBottom"
        >
            <span>{{ unreadCount > 0 ? `Новые сообщения · ${unreadCount}` : 'К новым сообщениям' }}</span>
            <span class="jump-arrow">↓</span>
        </button>
    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import ChatMessage from './ChatMessage.vue'
import ChatPreloader from '@/components/chat/ChatPreloader.vue'
import { useChatSuggestions } from '@/composables/useChatSuggestions'

const props = defineProps({
    messages: { type: Array, required: true },
    isLoadingHistory: { type: Boolean, default: false },
    userInitials: { type: String, default: 'ГМ' },
    userName: { type: String, default: '' },
    selectionMode: { type: Boolean, default: false },
    selectedIds: { type: Array, default: () => [] },
    isStreaming: { type: Boolean, default: false },
    activeMessageId: { type: String, default: null },
})

defineEmits(['regenerate', 'copy', 'feedback', 'followup', 'suggest', 'toggle-select'])

/** Проп иногда приходит как Ref — приводим к boolean. */
const showHistoryLoader = computed(() => {
    const v = props.isLoadingHistory
    if (typeof v === 'boolean') return v
    if (v && typeof v === 'object' && 'value' in v) return Boolean(v.value)
    return Boolean(v)
})

const scrollEl = ref(null)
const innerEl = ref(null)
const isAtBottom = ref(true)
const showJump = ref(false)
const unreadCount = ref(0)
const STICKY_THRESHOLD = 120

const { suggestions } = useChatSuggestions()

function distanceFromBottom() {
    const el = scrollEl.value
    if (!el) return 0
    return el.scrollHeight - el.scrollTop - el.clientHeight
}

function onScroll() {
    const dist = distanceFromBottom()
    isAtBottom.value = dist < STICKY_THRESHOLD
    if (isAtBottom.value) {
        showJump.value = false
        unreadCount.value = 0
    }
}

/**
 * Программный скролл к низу.
 *
 * Двойной requestAnimationFrame нужен из-за гонки между `nextTick` Vue и
 * фактическим layout'ом браузера: при load истории/смене треда `ChatMessage`'ы
 * монтируются каскадно (каждый со своими async children — графики, таблицы),
 * и в первом тике `scrollHeight` ещё не отражает полной высоты. Первый rAF
 * ждёт пока браузер закоммитит DOM-вставки от `nextTick`, второй — пока
 * layout пересчитает scrollHeight с учётом этих новых нод. Без этого
 * чат при перезагрузке/смене треда иногда оставался прокрученным наверх.
 *
 * `force=true` использует instant-jump в обход CSS `scroll-behavior: smooth`,
 * чтобы не было видно анимации «снизу вверх» в случае, когда мы догоняем
 * выросший контент.
 */
function scrollToBottomNow(force = false) {
    if (!scrollEl.value) return
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            const el = scrollEl.value
            if (!el) return
            if (force && typeof el.scrollTo === 'function') {
                el.scrollTo({ top: el.scrollHeight, behavior: 'instant' })
            } else {
                el.scrollTop = el.scrollHeight
            }
        })
    })
}

function jumpToBottom() {
    if (!scrollEl.value) return
    scrollEl.value.scrollTo({
        top: scrollEl.value.scrollHeight,
        behavior: 'smooth',
    })
    isAtBottom.value = true
    showJump.value = false
    unreadCount.value = 0
}

let resizeObserver = null

onMounted(() => {
    // Принудительный instant-jump: при mount'е компонент уже может оказаться
    // с готовой историей (например, ChatPage переоткрылся со state'ом),
    // и нам надо встать в конец без smooth-анимации, иначе пользователь
    // увидит, что чат «перепрыгивает» с верха в низ.
    nextTick(() => scrollToBottomNow(true))
    // Наблюдаем за ВНУТРЕННИМ контейнером (.messages-inner): его высота растёт
    // при наполнении ответа (текст, карточки и таймлайн процесса), тогда как
    // у самого скролл-контейнера .messages box-size фиксирован (flex:1) и RO
    // на рост контента не реагировал. Догоняем низ только если пользователь
    // сам не ушёл вверх (isAtBottom) — тогда чтение другого места не сбивается.
    const observed = innerEl.value || scrollEl.value
    if (typeof ResizeObserver !== 'undefined' && observed) {
        resizeObserver = new ResizeObserver(() => {
            if (isAtBottom.value) {
                scrollToBottomNow()
            }
        })
        resizeObserver.observe(observed)
    }
})

onBeforeUnmount(() => {
    if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null
    }
})

watch(
    () => props.messages.length,
    async (next, prev) => {
        await nextTick()
        // Смена треда: было пусто, стало N — это load истории или переключение
        // диалога. Здесь всегда хотим показать последнее сообщение, не оглядываясь
        // на «был ли юзер внизу» (старое значение isAtBottom — мусор из прошлого треда).
        if ((prev || 0) === 0 && next > 0) {
            isAtBottom.value = true
            showJump.value = false
            unreadCount.value = 0
            scrollToBottomNow(true)
            return
        }
        if (isAtBottom.value) {
            scrollToBottomNow()
        } else if (next > (prev || 0)) {
            unreadCount.value += next - (prev || 0)
            showJump.value = true
        }
    }
)

// Когда лоадер истории уходит (false), DOM наконец отрисовал реальные
// ChatMessage'ы — это правильный момент чтобы насильно прыгнуть в конец.
// До этого scrollHeight почти равен высоте лоадера и любой scrollToBottomNow
// бесполезен. Watch на показ/скрытие лоадера ловит этот переход независимо
// от того, изменилась ли длина messages (она могла быть N→N при обновлении истории).
watch(
    showHistoryLoader,
    async (loading, wasLoading) => {
        if (wasLoading && !loading && props.messages.length > 0) {
            await nextTick()
            isAtBottom.value = true
            showJump.value = false
            unreadCount.value = 0
            scrollToBottomNow(true)
        }
    }
)

watch(
    () => props.messages.map(m => {
        const textLen = m.text ? m.text.length : 0
        const cards = Array.isArray(m.cards) ? m.cards.length : 0
        const lists = Array.isArray(m.lists) ? m.lists.length : 0
        const callouts = Array.isArray(m.callouts) ? m.callouts.length : 0
        const followups = m.followups ? 1 : 0
        // progressSteps/progress растут во время работы агента (таймлайн
        // процесса) — без них долгая фаза «Анализ запроса» наполняла высоту,
        // но скролл не догонял низ.
        const steps = Array.isArray(m.progressSteps) ? m.progressSteps.length : 0
        const progressLen = m.progress ? m.progress.length : 0
        return `${textLen}-${cards}-${lists}-${callouts}-${followups}-${steps}-${progressLen}-${m.status}`
    }).join('|'),
    async () => {
        await nextTick()
        if (!scrollEl.value) return
        if (isAtBottom.value) {
            scrollToBottomNow()
        } else {
            showJump.value = true
        }
    }
)
</script>

<style scoped>
.messages-wrap {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    position: relative;
    background: white;
}

.messages {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 16px 24px 20px;
    background: white;
    scroll-behavior: smooth;
}

.messages-inner {
    max-width: 760px;
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 22px;
}

.history-loader {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: min(420px, 55vh);
}

.history-loader :deep(.preloader) {
    min-height: auto;
    padding: 0;
}

.history-loader :deep(.label) {
    color: #23282C;
    font-weight: 500;
}

.jump-button {
    position: absolute;
    left: 50%;
    bottom: 14px;
    transform: translateX(-50%);
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    border-radius: 18px;
    border: 1px solid #e0e0e0;
    background: white;
    color: #23282C;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(21, 26, 34, 0.15);
    z-index: 4;
}

.jump-button:hover {
    border-color: #23282C;
}

.jump-arrow {
    font-weight: 700;
}

.empty {
    max-width: 640px;
    margin: 24px auto;
    text-align: center;
    color: #23282C;
}

.empty-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
}

.empty-text {
    font-size: 13px;
    line-height: 1.6;
    color: #5f5f5f;
    margin: 0 auto 18px;
    max-width: 480px;
}

.empty-chips {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
    text-align: left;
}

.empty-chip {
    font-family: inherit;
    font-size: 13px;
    line-height: 1.45;
    padding: 10px 14px;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    background: white;
    color: #23282C;
    cursor: pointer;
    transition: border-color 0.15s ease;
    text-align: left;
}

.empty-chip:hover {
    border-color: #23282C;
}

@media (max-width: 768px) {
    .messages {
        padding: 16px 16px 20px;
        gap: 18px;
    }
}
</style>
